import { Grid } from '@mui/material';
import Box from '@mui/material/Box';
import Button from 'common/Button';
import ButtonSecondary from 'common/ButtonSecondary';
import FormModal from 'common/FormModal';
import FormStyle from 'common/FormStyle';
import ButtonLoader from 'common/Loaders/ButtonLoader';
import StickyActionFooter from 'common/StickyActionFooter';
import OutOfOfficeFormData from 'pages/Business/OutOfOffice/OutOfOfficeFormData';
import ProviderPopup from 'pages/Business/Provider/ProviderPopup';
import { useAuth } from 'context/AuthContext';

import React, { useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';
import { businessData as businessDataAtom } from 'atoms';

const OutOfOfficePopup = ({ open, onClose, getOooList, selectedOoo, providers, getProviders }) => {
    const { token } = useAuth();
    const [businessData] = useRecoilState(businessDataAtom)
    const [initialFormState, setInitialFormState] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [openProviderPopup, setOpenProviderPopup] = useState(false);
    const handleSubmit = async (e) => {
        try {
            e.preventDefault();

            if (!isFormValid()) return;

            setIsSubmitting(true);
            

            if (selectedOoo && selectedOoo.id) {
                // Handle business-level out of office (when provider is 'business')
                const updateData = {
                    date_start: formData.start_date + ' ' + formData.start_time,
                    date_end: formData.end_date + ' ' + formData.end_time,
                };

                // Only add provider_id if it's not the business option
                if (formData.provider !== 'business') {
                    updateData.provider_id = formData.provider;
                } else {
                    // If updating to business level, remove provider_id
                    updateData.provider_id = null;
                }

                // Validate access token
                if (!token) {
                    console.error('No authentication token found');
                    setIsSubmitting(false);
                    return;
                }

                // Call backend API to update provider_ooo instead of direct Supabase call
                const response = await fetch(
                    `${process.env.REACT_APP_SERVER_URL}/api/providers/provider-ooo`,
                    {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                        },
                        body: JSON.stringify({ id: selectedOoo.id, ...updateData }),
                    },
                );

                const result = await response.json();

                if (!response.ok || result.error) {
                    console.error('Error updating out of office:', result.error || result.message);
                    setIsSubmitting(false);
                    return;
                }

                getOooList().then(() => {
                    resetForm();
                    onClose();
                });

                return;
            }

            // Validate access token
            if (!token) {
                console.error('No authentication token found');
                setIsSubmitting(false);
                return;
            }

            // Prepare request body with separate date and time fields
            const requestBody = {
                start_date: formData.start_date,
                start_time: formData.start_time,
                end_date: formData.end_date,
                end_time: formData.end_time,
                provider: formData.provider === 'business' ? null : formData.provider,
                business_id: businessData?.id
            };

            // Make API call to backend
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/add-ooo`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(requestBody)
                }
            );

            const data = await response.json();

            if (!response.ok || data.error) {
                console.error('Error adding out of office:', data.error || data.message);
                setIsSubmitting(false);
                return;
            }

            getOooList().then(() => {
                resetForm();
                onClose();
            });
        } catch (error) {
            console.error('Error in handleSubmit:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const formObject = {
        provider: '',
        start_date: '',
        start_time: '',
        end_date: '',
        end_time: '',
    };

    const [formData, setFormData] = useState(formObject);

    const formDateInDateText = (date) => {
        if (!date) return '';
        return date.split('T')[0];
    };

    const formDateInTimeText = (date) => {
        if (!date) return '';
        let fragments = date.split('T')[1].split(':');
        return fragments[0] + ':' + fragments[1];
    };

    const resetForm = () => {
        setFormData(formObject);
    };

    useEffect(() => {
        if (open) {
            setInitialFormState(JSON.stringify(formData)); // Store initial form data
        }
    }, [open]);

    useEffect(() => {
        if (selectedOoo) {
            const newFormData = {
                provider: selectedOoo.provider_id || 'business', // Default to 'business' if no provider_id
                start_date: formDateInDateText(selectedOoo.date_start),
                start_time: formDateInTimeText(selectedOoo.date_start),
                end_date: formDateInDateText(selectedOoo.date_end),
                end_time: formDateInTimeText(selectedOoo.date_end),
            };
            setFormData(newFormData);
            
            // Set initial form state after loading the actual data
            setInitialFormState(JSON.stringify(newFormData));
        } else {
            const defaultFormData = formObject;
            setFormData(defaultFormData);
            setInitialFormState(JSON.stringify(defaultFormData));
        }
    }, [selectedOoo]);
    const handleDelete = async () => {
        const confirmDelete = window.confirm('Are you sure you want to delete this out-of-office entry?');

        if (!confirmDelete) {
            return;
        }

        try {
            if (!selectedOoo?.id) {
                alert('No out-of-office entry selected.');
                return;
            }

            // Validate access token
            if (!token) {
                console.error('No authentication token found');
                alert('Authentication error. Please log in again.');
                return;
            }

            // Call backend API to delete provider_ooo instead of direct Supabase call
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/provider-ooo`,
                {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({ id: selectedOoo.id }),
                },
            );

            const result = await response.json().catch(() => ({}));

            if (!response.ok || result.error) {
                console.error('Failed to delete out-of-office entry:', result.error || result.message);
                alert('Failed to delete the entry. Please try again.');
                return;
            }

            // Refresh the data after successful deletion
            getOooList().then(() => {
                onClose();
            });
        } catch (error) {
            console.error('Error deleting out-of-office entry:', error);
            alert('Failed to delete the entry. Please try again.');
        }
    };

    const handleClose = ()=>{
        const hasUnsavedChanges = JSON.stringify(formData) !== initialFormState;

        if (hasUnsavedChanges) {
            const confirmExit = window.confirm('You have unsaved changes. Do you really want to close?');
            if (!confirmExit) return;
        }
        
        setIsSubmitting(false);
        onClose();
        resetForm();
    }

    const isFormValid = () => {
        return (
            formData.provider !== '' && 
            formData.start_date !== '' && 
            formData.start_time !== '' && 
            formData.end_date !== '' && 
            formData.end_time !== ''
        );
    };
    
    return (
        <>
            <FormModal 
                open={open} 
                onClose={handleClose} 
                title="Schedule an Out of Office"
                stickyFooter={
                    <StickyActionFooter
                        onCancel={handleClose}
                        onSave={handleSubmit}
                        cancelText="Cancel"
                        saveText={selectedOoo ? "Update Out of Office" : "Schedule Out of Office"}
                        isLoading={isSubmitting}
                        disabled={!isFormValid()}
                        showDelete={!!selectedOoo}
                        onDelete={handleDelete}
                        deleteText="Delete Out of Office"
                    />
                }
            >
                <FormStyle onSubmit={handleSubmit}>
                    <OutOfOfficeFormData 
                        formData={formData} 
                        setFormData={setFormData} 
                        providers={providers}
                        onAddProvider={() => setOpenProviderPopup(true)}
                        businessData={businessData}
                    />
                </FormStyle>
            </FormModal>

            <ProviderPopup
                open={openProviderPopup}
                onClose={() => setOpenProviderPopup(false)}
                getProviderList={getProviders}
                selectedProvider={null}
                setSelectedProvider={() => {}}
                formState="add"
                setFormState={() => {}}
                businessId={businessData?.id}
                loading={false}
                setProviderLoading={() => {}}
            />
        </>
    );
};

export default OutOfOfficePopup;
